import React, { useState } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import {
    Plus, Trash2, FileText, CheckCircle, XCircle, Edit2,
    ArrowLeft, Eye, Save, GripVertical, Type, List, Upload, MessageSquare, CheckSquare
} from 'lucide-react';
import { ApplicationType, ApplicationForm, FormField, FormFieldType } from '../../types';

export const AgentForms: React.FC = () => {
    const { user } = useAuth();
    const { applicationForms, addApplicationForm, updateApplicationForm, toggleApplicationForm } = useData();
    const [isCreating, setIsCreating] = useState(false);
    const [editingFormId, setEditingFormId] = useState<string | null>(null);
    const [previewMode, setPreviewMode] = useState(false); // If true, show preview modal

    const myForms = applicationForms.filter(f => f.agentId === user?.id);
    const activeForm = editingFormId ? applicationForms.find(f => f.id === editingFormId) : null;

    // --- Create New Form State ---
    const [newForm, setNewForm] = useState({
        title: '',
        type: 'CASH' as ApplicationType,
        description: ''
    });

    // --- Form Builder Local State (when editing) ---
    // We keep a local copy of fields to avoid constant DB calls, save only on "Save"
    const [builderFields, setBuilderFields] = useState<FormField[]>([]);

    // Initialize builder when entering edit mode
    React.useEffect(() => {
        if (activeForm) {
            setBuilderFields(activeForm.fields || []);
        }
    }, [activeForm]);


    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        const success = await addApplicationForm({
            agentId: user.id,
            title: newForm.title,
            type: newForm.type,
            description: newForm.description,
            isActive: true,
            fields: []
        });

        if (success) {
            setIsCreating(false);
            setNewForm({ title: '', type: 'CASH', description: '' });
        }
    };

    const handleSaveFields = async () => {
        if (!editingFormId) return;
        await updateApplicationForm(editingFormId, { fields: builderFields });
        // Optional: Toast success
    };

    const addField = (type: FormFieldType) => {
        const newField: FormField = {
            id: `field-${Date.now()}`,
            type,
            label: type === 'file' ? 'Upload Document' : 'New Question',
            required: false,
            options: type === 'select' || type === 'multiselect' ? ['Option 1', 'Option 2'] : undefined,
            placeholder: ''
        };
        setBuilderFields([...builderFields, newField]);
    };

    const updateBuilderField = (id: string, updates: Partial<FormField>) => {
        setBuilderFields(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
    };

    const removeField = (id: string) => {
        setBuilderFields(prev => prev.filter(f => f.id !== id));
    };

    const sidebarItems = [
        { id: 'dashboard', label: 'Back to Dashboard', icon: XCircle, onClick: () => window.location.hash = '#/agent/dashboard' }
    ];

    if (editingFormId && activeForm) {
        return (
            <DashboardLayout title={`Edit: ${activeForm.title}`} activeTab="forms" sidebarItems={sidebarItems} onTabChange={() => { }}>
                <div className="max-w-5xl mx-auto py-8 px-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <Button variant="ghost" onClick={() => setEditingFormId(null)}><ArrowLeft className="w-5 h-5 mr-2" /> Back</Button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{activeForm.title}</h1>
                                <p className="text-gray-500">{activeForm.type} • {activeForm.description}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setPreviewMode(true)}><Eye className="w-4 h-4 mr-2" /> Preview</Button>
                            <Button onClick={handleSaveFields}><Save className="w-4 h-4 mr-2" /> Save Form</Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Toolbox */}
                        <div className="lg:col-span-1 space-y-4">
                            <Card className="p-4">
                                <h3 className="font-bold text-gray-900 mb-4">Add Fields</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    <button className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm" onClick={() => addField('text')}>
                                        <Type className="w-4 h-4 text-blue-500" /> Text
                                    </button>
                                    <button className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm" onClick={() => addField('textarea')}>
                                        <MessageSquare className="w-4 h-4 text-blue-500" /> Long Text
                                    </button>
                                    <button className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm" onClick={() => addField('select')}>
                                        <List className="w-4 h-4 text-purple-500" /> Dropdown
                                    </button>
                                    <button className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm" onClick={() => addField('multiselect')}>
                                        <CheckSquare className="w-4 h-4 text-purple-500" /> Multi-Select
                                    </button>
                                    <button className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm" onClick={() => addField('file')}>
                                        <Upload className="w-4 h-4 text-orange-500" /> Upload
                                    </button>
                                    <button className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm" onClick={() => addField('checkbox')}>
                                        <CheckCircle className="w-4 h-4 text-green-500" /> Checkbox
                                    </button>
                                </div>
                            </Card>
                            <Card className="p-4 bg-blue-50/50 border-blue-100">
                                <h4 className="font-bold text-blue-900 text-sm mb-2">Instructions</h4>
                                <p className="text-xs text-blue-800">
                                    Drag and drop support coming soon. Currently, click to add fields to the bottom.
                                    Remember to click "Save Form" to persist your changes.
                                </p>
                            </Card>
                        </div>

                        {/* Form Canvas */}
                        <div className="lg:col-span-2 space-y-4 pb-20">
                            {builderFields.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
                                    <p className="text-gray-400">Add fields from the toolbox to start building your form.</p>
                                </div>
                            ) : (
                                builderFields.map((field, index) => (
                                    <Card key={field.id} className="p-4 relative hover:shadow-md transition-shadow group border-l-4 border-l-blue-500">
                                        <div className="absolute right-4 top-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => removeField(field.id)} className="text-red-400 hover:text-red-600">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            {/* Header / Type */}
                                            <div className="flex items-center gap-2 text-xs text-gray-400 uppercase font-bold tracking-wider">
                                                <GripVertical className="w-4 h-4 cursor-grab" />
                                                {field.type} Field
                                            </div>

                                            {/* Configuration */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Question Label</label>
                                                    <input
                                                        type="text"
                                                        className="w-full p-2 border border-gray-300 rounded font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                                        value={field.label}
                                                        onChange={e => updateBuilderField(field.id, { label: e.target.value })}
                                                        placeholder="e.g. Upload your ID Document"
                                                    />
                                                </div>

                                                {(field.type === 'text' || field.type === 'textarea') && (
                                                    <div className="md:col-span-2">
                                                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Placeholder</label>
                                                        <input
                                                            type="text"
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            value={field.placeholder || ''}
                                                            onChange={e => updateBuilderField(field.id, { placeholder: e.target.value })}
                                                            placeholder="Placeholder text..."
                                                        />
                                                    </div>
                                                )}

                                                {(field.type === 'select' || field.type === 'multiselect') && (
                                                    <div className="md:col-span-2 bg-gray-50 p-3 rounded">
                                                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Options (Comma Separated)</label>
                                                        <input
                                                            type="text"
                                                            className="w-full p-2 border border-gray-300 rounded text-sm"
                                                            value={field.options?.join(', ') || ''}
                                                            onChange={e => updateBuilderField(field.id, { options: e.target.value.split(',').map(s => s.trim()) })}
                                                            placeholder="Option 1, Option 2, Option 3"
                                                        />
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-2 mt-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`req-${field.id}`}
                                                        checked={field.required}
                                                        onChange={e => updateBuilderField(field.id, { required: e.target.checked })}
                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <label htmlFor={`req-${field.id}`} className="text-sm text-gray-700">Required Field</label>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Preview Modal */}
                    {previewMode && (
                        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">{activeForm.title}</h2>
                                        <p className="text-sm text-gray-500">Student Preview Mode</p>
                                    </div>
                                    <button onClick={() => setPreviewMode(false)} className="text-gray-400 hover:text-gray-600"><XCircle className="w-6 h-6" /></button>
                                </div>
                                <div className="p-8 overflow-y-auto bg-gray-50/30 flex-1 space-y-6">
                                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg text-sm text-blue-800 mb-6">
                                        {activeForm.description || "Please complete the form below."}
                                    </div>

                                    {builderFields.map(field => (
                                        <div key={field.id}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {field.label} {field.required && <span className="text-red-500">*</span>}
                                            </label>

                                            {field.type === 'text' && (
                                                <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder={field.placeholder} />
                                            )}

                                            {field.type === 'textarea' && (
                                                <textarea className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" rows={3} placeholder={field.placeholder}></textarea>
                                            )}

                                            {field.type === 'select' && (
                                                <select className="w-full p-3 border border-gray-300 rounded-lg bg-white">
                                                    <option value="">Select an option...</option>
                                                    {field.options?.map(opt => <option key={opt}>{opt}</option>)}
                                                </select>
                                            )}

                                            {field.type === 'multiselect' && (
                                                <div className="space-y-2 border border-gray-200 p-3 rounded-lg bg-white">
                                                    {field.options?.map(opt => (
                                                        <div key={opt} className="flex items-center gap-2">
                                                            <input type="checkbox" className="rounded text-blue-600" />
                                                            <span className="text-sm text-gray-700">{opt}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {field.type === 'checkbox' && (
                                                <div className="flex items-center gap-2">
                                                    <input type="checkbox" className="rounded text-blue-600" />
                                                    <span className="text-sm text-gray-700">Yes</span>
                                                </div>
                                            )}

                                            {field.type === 'file' && (
                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white hover:bg-gray-50 transition-colors cursor-pointer">
                                                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                    <p className="text-sm text-gray-600">Click to upload document</p>
                                                    <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG accepted</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <div className="border-t border-gray-100 pt-6 mt-8">
                                        <Button className="w-full py-3 text-lg">Submit Application</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </DashboardLayout>
        );
    }

    // --- LIST VIEW ---
    return (
        <DashboardLayout
            title="Application Forms"
            activeTab="forms"
            sidebarItems={sidebarItems}
            onTabChange={() => { }}
        >
            <div className="max-w-4xl mx-auto py-8 px-4">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Application Forms</h1>
                        <p className="text-gray-500">Manage the application forms students use to apply.</p>
                    </div>
                    <Button onClick={() => setIsCreating(true)}><Plus className="w-4 h-4 mr-2" /> Create New</Button>
                </div>

                {isCreating && (
                    <Card className="mb-8 p-6 border-blue-100 bg-blue-50/30 animate-scaleIn">
                        <h3 className="font-bold text-gray-900 mb-4">Create New Form</h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Form Title</label>
                                    <input required type="text" className="w-full p-2 border rounded" placeholder="e.g. 2025 NSFAS Application" value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <select className="w-full p-2 border rounded bg-white" value={newForm.type} onChange={e => setNewForm({ ...newForm, type: e.target.value as ApplicationType })}>
                                        <option value="CASH">Cash / Private</option>
                                        <option value="NSFAS">NSFAS</option>
                                        <option value="BURSARY">Bursary (Other)</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                                <textarea className="w-full p-2 border rounded" rows={2} placeholder="Brief instructions for the student..." value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })} />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>Cancel</Button>
                                <Button type="submit">Create Form</Button>
                            </div>
                        </form>
                    </Card>
                )}

                <div className="grid gap-4">
                    {myForms.length > 0 ? myForms.map(form => (
                        <Card key={form.id} className="p-4 flex flex-col md:flex-row items-center justify-between hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-4 mb-4 md:mb-0">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${form.type === 'NSFAS' ? 'bg-green-100 text-green-600' : (form.type === 'BURSARY' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600')}`}>
                                    <FileText className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900">{form.title}</h3>
                                    <p className="text-sm text-gray-500">{form.type} • {form.fields?.length || 0} Fields</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditingFormId(form.id)}>
                                    <Edit2 className="w-4 h-4 mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant={form.isActive ? "outline" : "primary"} onClick={() => toggleApplicationForm(form.id)}>
                                    {form.isActive ? 'Disable' : 'Enable'}
                                </Button>
                            </div>
                        </Card>
                    )) : (
                        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
                            <FileText className="w-12 h-12 mx-auto mb-2 opacity-20" />
                            <p>No application forms created yet.</p>
                            <p className="text-xs">Create forms to allow specific booking types (e.g. NSFAS).</p>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};
